import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
  ScrollReveal,
  ScrollRevealStagger,
  ScrollRevealItem,
  EASE_SMOOTH,
  VIEWPORT,
} from './ScrollReveal';
import { CalendarRange } from 'lucide-react';
import calcHeroImg from '../assets/images/12.png';
import './loan-calculator.css';

/**
 * Short-term loan: single repayment at end of tenure.
 * Interest = principal × daily rate × tenure days
 * Example: ₹1,00,000 × 1% × 30 days = ₹30,000 interest
 */
const calculateShortTermLoan = (principal, dailyRate, tenureDays) => {
  const interest = Math.round(principal * (dailyRate / 100) * tenureDays);
  const totalRepayment = principal + interest;
  return { interest, totalRepayment };
};

const LoanCalculator = () => {
  const [amount, setAmount] = useState(5000);
  const [rate, setRate] = useState(1);
  const [tenure, setTenure] = useState(30);
  const [interest, setInterest] = useState(0);
  const [totalRepayment, setTotalRepayment] = useState(0);

  useEffect(() => {
    const { interest: intAmt, totalRepayment: total } = calculateShortTermLoan(
      amount,
      rate,
      tenure
    );
    setInterest(intAmt);
    setTotalRepayment(total);
  }, [amount, rate, tenure]);

  const interestRatio = useMemo(
    () =>
      totalRepayment > 0 ? (interest / totalRepayment) * 100 : 0,
    [interest, totalRepayment]
  );

  const getProgressStyle = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return {
      background: `linear-gradient(to right, #00b4d8 0%, #00b4d8 ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`,
    };
  };

  const sliders = [
    {
      id: 'lc-amount',
      label: 'Loan Amount',
      min: 5000,
      max: 100000,
      step: 1000,
      value: amount,
      onChange: setAmount,
      display: `₹${amount.toLocaleString('en-IN')}`,
      hints: ['₹5K', '₹1L'],
    },
    {
      id: 'lc-rate',
      label: 'Daily Rate',
      min: 0.9,
      max: 1.5,
      step: 0.1,
      value: rate,
      onChange: setRate,
      display: `${Number(rate).toFixed(1)}% / day`,
      hints: ['0.9%', '1.5%'],
      cyan: true,
    },
    {
      id: 'lc-tenure',
      label: 'Tenure',
      min: 7,
      max: 365,
      step: 1,
      value: tenure,
      onChange: setTenure,
      display: `${tenure} day${tenure === 1 ? '' : 's'}`,
      hints: ['7 days', '365 days'],
    },
  ];

  return (
    <section className="lc-section" id="loan-calculator">
      <div className="lc-bg" aria-hidden="true">
        <div className="lc-bg-gradient" />
        <div className="lc-bg-mesh" />
        <div className="lc-bg-dots" />
        <span className="lc-bg-orb lc-bg-orb--1" />
        <span className="lc-bg-orb lc-bg-orb--2" />
        <span className="lc-bg-orb lc-bg-orb--3" />
      </div>

      <div className="lc-container">
        <ScrollReveal className="lc-shell" y={44} duration={0.6}>
          <aside className="lc-visual" aria-label="Loan calculator">
            <div className="lc-visual-glow" aria-hidden="true" />
            <div className="lc-visual-copy">
              <span className="lc-badge">Ultra Short Term Loan</span>
              <h2 className="lc-heading">
                Smart <span>Loan</span> Calculator
              </h2>
              <p className="lc-lead">
                Adjust amount, rate and tenure to see your estimated total repayment.
              </p>
            </div>

            <div className="lc-visual-total">
              <span className="lc-visual-total-label">Estimated total due</span>
              <strong className="lc-visual-total-amt">
                ₹{totalRepayment.toLocaleString('en-IN')}
              </strong>
            </div>

            <motion.img
              src={calcHeroImg}
              alt="Financial advisor with calculator"
              className="lc-visual-img"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={VIEWPORT}
              transition={{ duration: 0.6, delay: 0.2, ease: EASE_SMOOTH }}
            />
          </aside>

          <div className="lc-calc">
            <ScrollRevealStagger className="lc-sliders" stagger={0.12}>
              {sliders.map((s) => (
                <ScrollRevealItem key={s.id} className="lc-field" y={22}>
                  <div className="lc-field-top">
                    <label htmlFor={s.id}>{s.label}</label>
                    <output
                      htmlFor={s.id}
                      className={s.cyan ? 'lc-val lc-val-cyan' : 'lc-val'}
                    >
                      {s.display}
                    </output>
                  </div>
                  <input
                    id={s.id}
                    type="range"
                    min={s.min}
                    max={s.max}
                    step={s.step}
                    value={s.value}
                    onChange={(e) => s.onChange(Number(e.target.value))}
                    style={getProgressStyle(s.value, s.min, s.max)}
                    className="lc-range"
                  />
                  <div className="lc-hints">
                    <span>{s.hints[0]}</span>
                    <span>{s.hints[1]}</span>
                  </div>
                </ScrollRevealItem>
              ))}
            </ScrollRevealStagger>

            <ScrollReveal className="lc-results" delay={0.15} y={28}>
              <div className="lc-tenure-chip">
                <CalendarRange size={15} strokeWidth={2.2} aria-hidden="true" />
                <span>
                  {tenure} day{tenure === 1 ? '' : 's'} · Principal + interest
                </span>
              </div>

              <div className="lc-result-hero">
                <div className="lc-donut-wrap">
                  <svg viewBox="0 0 36 36" className="lc-donut" aria-hidden="true">
                    <path
                      className="lc-donut-bg"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="lc-donut-fill"
                      strokeDasharray={`${interestRatio}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                </div>
                <div className="lc-repay-box">
                  <span className="lc-repay-label">Repayment Amount</span>
                  <p className="lc-repay-amount">
                    ₹{' '}
                    <CountUp
                      end={totalRepayment}
                      duration={0.4}
                      separator=","
                      preserveValue
                    />
                  </p>
                </div>
              </div>

              <div className="lc-metrics">
                <div className="lc-metric">
                  <span className="lc-metric-label">Principal</span>
                  <strong>₹{amount.toLocaleString('en-IN')}</strong>
                </div>
                <div className="lc-metric lc-metric--interest">
                  <span className="lc-metric-label">Interest</span>
                  <strong>₹{interest.toLocaleString('en-IN')}</strong>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default LoanCalculator;
